import { isAxiosError } from 'axios';

import { httpPOST } from '../../../services/http';
import type { CadImportRequest, CadImportResponse } from './cadImport.worker';
import { CadImport } from './importCad';

/** A drawing that could not be imported; `message` is safe to show. */
export class CadImportError extends Error {}

const extension = (file: File) => file.name.split('.').pop()?.toLowerCase() ?? '';

export const isCadFile = (file: File) => ['dxf', 'dwg'].includes(extension(file));

/** DWG is converted to DXF by the backend (GNU LibreDWG); DXF is read as it is. */
async function dxfBytes(file: File): Promise<ArrayBuffer> {
  if (extension(file) !== 'dwg') return file.arrayBuffer();
  const form = new FormData();
  form.append('file', file);
  try {
    const response = await httpPOST('/cad/convert', form, { responseType: 'arraybuffer' });
    return response.data as ArrayBuffer;
  } catch (error) {
    if (isAxiosError(error) && error.response?.data instanceof ArrayBuffer) {
      const message = new TextDecoder().decode(error.response.data);
      if (message) throw new CadImportError(message);
    }
    throw error;
  }
}

function importInWorker(bytes: ArrayBuffer): Promise<CadImport> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./cadImport.worker.ts', import.meta.url), {
      type: 'module',
      name: 'cad-import-worker',
    });
    worker.onmessage = (event: MessageEvent<CadImportResponse>) => {
      worker.terminate();
      if (event.data.ok) resolve(event.data.result);
      else reject(new CadImportError(event.data.error));
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new CadImportError(event.message || 'The drawing could not be read.'));
    };
    const request: CadImportRequest = { bytes };
    worker.postMessage(request, [bytes]);
  });
}

/**
 * Reads a DXF or DWG drawing into layers of polygons (see importDxf). `dxf` is the drawing as DXF
 * (a DWG converted), to be stored with it.
 */
export async function importCadFile(file: File): Promise<{ result: CadImport; dxf: Blob }> {
  const bytes = await dxfBytes(file);
  // Copied before the bytes move to the worker.
  const dxf = new Blob([bytes]);
  return { result: await importInWorker(bytes), dxf };
}

/** A drawing's stored CAD file (gzipped DXF) read again. */
export async function importCadSource(url: string): Promise<CadImport> {
  const response = await fetch(url);
  if (!response.ok || !response.body)
    throw new CadImportError(`The drawing's file could not be loaded (${response.status}).`);
  const bytes = await new Response(response.body.pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
  return importInWorker(bytes);
}

/** Gzips a DXF for storing it with the drawing (city drawings compress about 5:1). */
export async function gzipDxf(dxf: Blob): Promise<Blob> {
  return new Response(dxf.stream().pipeThrough(new CompressionStream('gzip'))).blob();
}
